import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  PERMISSION_MANAGEMENT_CREATE,
  PERMISSION_MANAGEMENT_UPDATE,
  PERMISSION_MANAGEMENT_VIEW,
} from "../../rolesPermissions";
import { rolesSearchSchema } from "./searchSchemas";
import type { RolesRouteFactory } from "./rolesRouteContext";

const RoleDetailScreen = lazyRouteComponent(
  () => import("../RoleDetailScreen"),
  "RoleDetailScreen",
);
const RoleFormScreen = lazyRouteComponent(
  () => import("../RoleFormScreen"),
  "RoleFormScreen",
);
const RolesListScreen = lazyRouteComponent(
  () => import("../RolesListScreen"),
  "RolesListScreen",
);

export const createRolesRoutes: RolesRouteFactory = ({ context, roles }) => {
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id/edit`,
    beforeLoad: requirePermission([PERMISSION_MANAGEMENT_UPDATE]),
    component: function Edit() {
      // Route-scoped params, not a manual pathname split: during the
      // navigate-away transition after a successful update, the global
      // location already points at the new URL while this route's own match
      // (and its params) only change once this component actually unmounts,
      // so it never briefly re-renders with the wrong id. basePath is a
      // runtime string, so the path template loses the literal type
      // TanStack Router needs to type params -- $id is guaranteed by the
      // route path itself.
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(RoleFormScreen, {
        repository: roles,
        basePath: context.basePath,
        roleId: id,
        permissionCatalog: context.permissionCatalog,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id`,
    beforeLoad: requirePermission([PERMISSION_MANAGEMENT_VIEW]),
    component: function Detail() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(RoleDetailScreen, {
        repository: roles,
        basePath: context.basePath,
        roleId: id,
        permissionCatalog: context.permissionCatalog,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: context.basePath,
      validateSearch: rolesSearchSchema,
      beforeLoad: requirePermission([PERMISSION_MANAGEMENT_VIEW]),
      component: () =>
        createElement(RolesListScreen, {
          repository: roles,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/create`,
      beforeLoad: requirePermission([PERMISSION_MANAGEMENT_CREATE]),
      component: () =>
        createElement(RoleFormScreen, {
          repository: roles,
          basePath: context.basePath,
          permissionCatalog: context.permissionCatalog,
        }),
    }),
    editRoute,
    detailRoute,
  ];
};
